// SUPER-ADMIN (2026-11-02): the platform operator workspace. Not a client company: the platform's
// own agency (`cp.agency.platform`), whose admins are the super admins and whose operators are
// staff. From here operators run clients: they are assigned clients, open one (deploy, monitor,
// talk to its Skillsmith through the existing operator routes), and every list or open of a
// client is a `cp.platform_audit` row.
//
//   GET  /v1/operator/workspace                              who I am here, the workspace's name
//   GET  /v1/operator/workspace/clients                      the clients I run (super admin: all)
//   POST /v1/operator/workspace/clients/{company_id}/open    open one: my operator access there
//   GET  /v1/operator/workspace/assignments                  staff and assignments (super admin)
//   POST /v1/operator/workspace/assignments                  assign an operator to a client
//   POST /v1/operator/workspace/assignments/{id}/revoke      end an assignment (and its access)
//   POST /v1/super-admin/claim                               claim a super-admin link (session)
//
// Who: a super admin (platform agency admin) sees and opens every client the platform agency
// holds a live operator grant on; an operator of the platform agency only the clients assigned to
// them (`cp.admit_client_operator`). Only a super admin assigns. An agency user of another agency
// is 403, anyone without an agency 404 (as every operator route).
import { z } from "zod";
import { Slug, Timestamp, Uuid } from "../primitives";
import { OnboardingCaseSummary } from "./client-onboarding";
import { defineRoute } from "./route";

const ws = "/v1/operator/workspace";
const operator = { feature: "core", tag: "operator", access: "operator" } as const;

export const OperatorWorkspaceRole = z.enum(["super_admin", "operator"]);

export const OperatorWorkspaceView = z
  .object({
    /** The platform operator's display name (`cp.platform_setting.operator_name`). */
    name: z.string(),
    agency_id: Uuid,
    user_id: Uuid,
    role: OperatorWorkspaceRole,
    /** Clients this person runs now (super admin: every client of the platform agency). */
    client_count: z.int().min(0),
  })
  .meta({ id: "OperatorWorkspaceView" });
export type OperatorWorkspaceView = z.infer<typeof OperatorWorkspaceView>;

/** Where a client is in its onboarding, from its own rows (never a stored flag). */
export const ClientOnboardingStatus = z.enum([
  /** No active owner yet (the owner's invitation is pending, or none was sent). */
  "awaiting_owner",
  /** An active owner, no digital employee working yet. */
  "setting_up",
  /** At least one digital employee is active. */
  "live",
]);

export const OperatorWorkspaceClient = z
  .object({
    company: z.object({ id: Uuid, slug: Slug, name: z.string() }),
    onboarding: z.object({
      status: ClientOnboardingStatus,
      owner_active: z.boolean(),
      pending_invitations: z.int().min(0),
      de_active: z.int().min(0),
      de_onboarding: z.int().min(0),
      /**
       * ONBOARDING-P1: the client's onboarding case (plan, intake, deadline, next follow-up);
       * null without one or while CLIENT_ONBOARDING is off.
       */
      case: OnboardingCaseSummary.nullable().optional(),
    }),
    /** The company's Skillsmith (system agent), null when it has none. */
    skillsmith: z
      .object({
        membership_id: Uuid,
        name: z.string(),
        /** The space route (`operator.systemAgents.space.get`); SKILLSMITH-LENS builds the lens. */
        space_path: z.string(),
      })
      .nullable(),
    /** The operator user ids assigned to this client (super admins see every client anyway). */
    assigned_user_ids: z.array(Uuid),
    /** The caller already holds operator access there (opened before); else `open` grants it. */
    opened: z.boolean(),
  })
  .meta({ id: "OperatorWorkspaceClient" });
export type OperatorWorkspaceClient = z.infer<typeof OperatorWorkspaceClient>;

export const OperatorWorkspaceClients = z
  .object({
    role: OperatorWorkspaceRole,
    data: z.array(OperatorWorkspaceClient),
    /**
     * The fleet-level surfaces the workspace links to (route ids of the existing operator
     * console): fleet reports and one-click actions. Super admins get every one.
     */
    surfaces: z.array(z.object({ route: z.string(), label: z.string() })),
  })
  .meta({ id: "OperatorWorkspaceClients" });
export type OperatorWorkspaceClients = z.infer<typeof OperatorWorkspaceClients>;

export const OperatorWorkspaceClientParams = z.object({ company_id: Uuid });

export const OperatorWorkspaceOpen = z.object({}).strict().meta({ id: "OperatorWorkspaceOpen" });

export const OperatorWorkspaceOpened = z
  .object({
    company: z.object({ id: Uuid, slug: Slug, name: z.string() }),
    /** True when this open granted the caller's operator access (false: already held). */
    granted: z.boolean(),
    /** The client's own app (`/c/{slug}`) and its Skillsmith space route, when it has one. */
    links: z.object({ app: z.string(), skillsmith_space: z.string().nullable() }),
  })
  .meta({ id: "OperatorWorkspaceOpened" });
export type OperatorWorkspaceOpened = z.infer<typeof OperatorWorkspaceOpened>;

export const OperatorStaffMember = z.object({
  user_id: Uuid,
  name: z.string(),
  email: z.string(),
  role: OperatorWorkspaceRole,
});

export const OperatorAssignmentView = z
  .object({
    id: Uuid,
    user_id: Uuid,
    company_id: Uuid,
    assigned_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "OperatorAssignmentView" });
export type OperatorAssignmentView = z.infer<typeof OperatorAssignmentView>;

export const OperatorAssignments = z
  .object({ staff: z.array(OperatorStaffMember), data: z.array(OperatorAssignmentView) })
  .meta({ id: "OperatorAssignments" });
export type OperatorAssignments = z.infer<typeof OperatorAssignments>;

export const OperatorAssignmentCreate = z
  .object({ user_id: Uuid, company_id: Uuid })
  .strict()
  .meta({ id: "OperatorAssignmentCreate" });

export const OperatorAssignmentParams = z.object({ id: Uuid });
export const OperatorAssignmentRevoke = z
  .object({})
  .strict()
  .meta({ id: "OperatorAssignmentRevoke" });

export const SuperAdminClaim = z
  .object({
    token: z
      .string()
      .max(64)
      .regex(/^cossa_[A-Za-z0-9_-]{43}$/),
  })
  .strict()
  .meta({ id: "SuperAdminClaim" });

export const SuperAdminClaimed = z
  .object({ agency_id: Uuid, user_id: Uuid, role: z.literal("super_admin") })
  .meta({ id: "SuperAdminClaimed" });

export const operatorWorkspaceRoutes = {
  "operator.workspace.get": defineRoute({
    ...operator,
    id: "operator.workspace.get",
    method: "get",
    path: ws,
    summary: "The platform operator workspace: the caller's role (super admin or operator) there",
    response: { status: 200, schema: OperatorWorkspaceView },
    errors: [],
    emits: [],
  }),
  "operator.workspace.clients.list": defineRoute({
    ...operator,
    id: "operator.workspace.clients.list",
    method: "get",
    path: `${ws}/clients`,
    summary:
      "The clients the caller runs (super admin: every client), with onboarding status and Skillsmith; audited",
    response: { status: 200, schema: OperatorWorkspaceClients },
    errors: [],
    emits: [],
  }),
  "operator.workspace.clients.open": defineRoute({
    ...operator,
    id: "operator.workspace.clients.open",
    method: "post",
    path: `${ws}/clients/{company_id}/open`,
    summary:
      "Open a client from the workspace: the caller's operator access there (granted when missing); audited",
    params: OperatorWorkspaceClientParams,
    body: OperatorWorkspaceOpen,
    response: { status: 200, schema: OperatorWorkspaceOpened },
    errors: [],
    emits: ["access.granted"],
  }),
  "operator.workspace.assignments.list": defineRoute({
    ...operator,
    id: "operator.workspace.assignments.list",
    method: "get",
    path: `${ws}/assignments`,
    summary: "The workspace's staff and their live client assignments (super admins only)",
    response: { status: 200, schema: OperatorAssignments },
    errors: [],
    emits: [],
  }),
  "operator.workspace.assignments.create": defineRoute({
    ...operator,
    id: "operator.workspace.assignments.create",
    method: "post",
    path: `${ws}/assignments`,
    summary: "Assign an operator of the workspace to a client (super admins only; audited)",
    body: OperatorAssignmentCreate,
    // A control-plane row and its `platform_audit` row; the operator's access in the client is
    // granted when they open it (`operator.workspace.clients.open`).
    safe: true,
    response: { status: 201, schema: OperatorAssignmentView },
    errors: ["conflict"],
    emits: [],
  }),
  "operator.workspace.assignments.revoke": defineRoute({
    ...operator,
    id: "operator.workspace.assignments.revoke",
    method: "post",
    path: `${ws}/assignments/{id}/revoke`,
    summary:
      "End an operator's client assignment: the row ends and their access there is revoked (super admins only; audited)",
    params: OperatorAssignmentParams,
    body: OperatorAssignmentRevoke,
    response: { status: 200, schema: OperatorAssignmentView },
    errors: [],
    emits: ["access.revoked"],
  }),
  "superAdmin.claim": defineRoute({
    feature: "core",
    tag: "operator",
    access: "session",
    id: "superAdmin.claim",
    method: "post",
    path: "/v1/super-admin/claim",
    summary:
      "Claim a single-use super-admin link with the signed-in account it names (30 minutes; audited)",
    body: SuperAdminClaim,
    // The platform agency's admin row and the audit row: control plane only, no company event.
    safe: true,
    response: { status: 200, schema: SuperAdminClaimed },
    errors: ["not_found", "conflict", "forbidden"],
    emits: [],
  }),
};
